import type {
  EchoflicksSessionEvent,
  EchoflicksSessionFramingEvent,
  EchoflicksSessionJourney,
  EchoflicksSessionMedia,
  EchoflicksVideoSession,
  I2VPromptAuditSummary,
} from "../types.js";
import {
  buildStoryboard,
  detectedMusicBpm,
  directorScriptOf,
  type SceneI2VAudit,
} from "../echoflicks/scriptStory.js";
import {
  ECHO_FEEDBACK_LIMITS,
  type FeedbackCollectorName,
  type FeedbackCollectorStatus,
  type FeedbackJsonObject,
  type FeedbackJsonValue,
  type FeedbackMetadataSnapshot,
} from "./feedbackContracts.js";
import {
  buildFeedbackFramingSnapshot,
  framingSnapshotToJson,
  type FramingInspectorSelection,
} from "./feedbackFramingSnapshot.js";
import type { FeedbackCategoryId } from "./feedbackTaxonomy.js";

import {
  buildNarrationTimeline,
  isNarrationTimeline,
} from "../echoflicks/narrationTimeline.js";
import type { SyncTimeline } from "../echoflicks/syncTimeline.js";
import { buildSyncTimeline } from "../echoflicks/syncTimeline.js";
import { readNarrationPlan } from "../echoflicks/narrationPlan.js";

type JsonRecord = Record<string, unknown>;
type SceneIdentity = string | number;

export type FeedbackMetadataInput = {
  record: EchoflicksVideoSession;
  position: {
    timestampMs: number;
    videoDurationMs?: number | null;
    videoWidth?: number | null;
    videoHeight?: number | null;
    playbackRate?: number | null;
  };
  categoryIds?: readonly FeedbackCategoryId[];
  envelope?: Record<string, unknown>;
  manifest?: unknown;
  sync?: SyncTimeline | null;
  finalVideoStoragePath?: string | null;
  media?: readonly EchoflicksSessionMedia[];
  journey?: EchoflicksSessionJourney | null;
  events?: readonly EchoflicksSessionEvent[];
  sceneI2VByScene?: ReadonlyMap<SceneIdentity, SceneI2VAudit> | null;
  /**
   * Exact scene/attempt selection from the already-authorized framing
   * inspector, when feedback is submitted while it is open (CROP-WB-003).
   */
  framingSelection?: FramingInspectorSelection | null;
  /** Test/migration injection. Normal callers omit this server-derived value. */
  capturedAt?: string;
};

export type FeedbackTimelineSource =
  | "timeline_contract"
  | "scene_timeline"
  | "storyboard"
  | "unavailable";

export type FeedbackTimelineConfidence =
  | "exact"
  | "derived"
  | "estimated"
  | "unavailable";

export type ResolvedFeedbackScene = {
  id: SceneIdentity | null;
  index: number;
  type: string | null;
  startFrame: number | null;
  endFrameExclusive: number | null;
  startMs: number;
  endMs: number;
  sourceFile: string | null;
  assetId: string | null;
  transition: string | null;
  transitionFrames: number | null;
  transitionPlacement: string | null;
  conformPolicy: string | null;
  visualDescription: string | null;
};

export type ResolvedFeedbackTimeline = {
  source: FeedbackTimelineSource;
  confidence: FeedbackTimelineConfidence;
  contractVersion: string | null;
  fps: number | null;
  targetFrames: number | null;
  targetDurationMs: number | null;
  timestampFrame: number | null;
  scene: ResolvedFeedbackScene | null;
  previousScene: ResolvedFeedbackScene | null;
  nextScene: ResolvedFeedbackScene | null;
  missing: string[];
};

type CollectorResult = {
  status: FeedbackCollectorStatus;
  missing: string[];
  data: FeedbackJsonObject;
};

type RawCollectorResult = Omit<CollectorResult, "data"> & { data: unknown };

const CATEGORY_COLLECTORS: Readonly<
  Partial<Record<FeedbackCategoryId, readonly FeedbackCollectorName[]>>
> = {
  framing_crop: ["media", "framing"],
  i2v_animation: ["i2v"],
  subject_expression: ["media", "i2v"],
  visual_artifact: ["i2v"],
  repetition: ["media"],
  media_selection: ["media"],
  timing_pacing: ["timeline", "audio"],
  audio_music: ["audio"],
};

const SENSITIVE_KEY_NAMES = new Set([
  "authorization",
  "cookie",
  "set_cookie",
  "api_key",
  "apikey",
  "access_token",
  "refresh_token",
  "id_token",
  "token",
  "client_secret",
  "password",
  "secret",
  "credentials",
  "credential",
  "webhook",
  "webhook_url",
  "signed_url",
  "presigned_url",
  "phone",
  "phone_number",
  "phone_e164",
  "email",
  "contact_email",
  "contact_phone",
]);

const SIGNED_OR_SECRET_URL =
  /(?:[?&](?:x-amz-[^=&#]+|x-goog-[^=&#]+|signature|sig|token|key|credential)=|\/hooks\/services\/)/i;

const SCENE_FALLBACK_MS = 3_000;
const MAX_CONTEXT_MEDIA = 20;
const MAX_ATTEMPTS = 10;

/**
 * Build one immutable, allowlisted diagnostic snapshot. Each collector fails
 * independently; a malformed optional artifact can never discard the operator
 * comment or another healthy collector.
 */
export function resolveFeedbackMetadata(
  input: FeedbackMetadataInput,
): FeedbackMetadataSnapshot {
  const timeline = resolveFeedbackTimeline(
    input.envelope,
    input.position.timestampMs,
    input.media ?? [],
    input.sceneI2VByScene,
  );
  const core = runCollector(() => collectCore(input));
  const timelineCollector = runCollector(() => collectTimeline(timeline));
  const i2v = runCollector(() => collectI2V(input, timeline));
  const media = runCollector(() => collectMedia(input, timeline));
  const audio = runCollector(() => collectAudio(input, timeline));
  const configuration = runCollector(() => collectConfiguration(input));
  const framing = runCollector(() => collectFraming(input));
  const results: Record<FeedbackCollectorName, CollectorResult> = {
    core,
    timeline: timelineCollector,
    i2v,
    media,
    audio,
    configuration,
    framing,
  };

  const required = new Set<FeedbackCollectorName>(["core", "timeline"]);
  for (const categoryId of input.categoryIds ?? []) {
    for (const collector of CATEGORY_COLLECTORS[categoryId] ?? []) {
      required.add(collector);
    }
  }

  const requiredResults = [...required].map((name) => results[name]);
  const hasEnrichment = Object.entries(results).some(
    ([name, result]) => name !== "core" && Object.keys(result.data).length > 0,
  );
  const status = requiredResults.every((result) => result.status === "complete")
    ? "complete"
    : hasEnrichment
      ? "partial"
      : "unavailable";

  const snapshot: FeedbackMetadataSnapshot = {
    status,
    capturedAt: normalizedCaptureTime(input.capturedAt),
    collectors: {
      core: summaryOf(core),
      timeline: summaryOf(timelineCollector),
      i2v: summaryOf(i2v),
      media: summaryOf(media),
      audio: summaryOf(audio),
      configuration: summaryOf(configuration),
      framing: summaryOf(framing),
    },
    core: core.data,
    timeline: timelineCollector.data,
    i2v: i2v.data,
    media: media.data,
    audio: audio.data,
    configuration: configuration.data,
    framing: framing.data,
  };
  assertNoSensitiveMetadata(snapshot);
  return snapshot;
}

/**
 * Freeze the framing correlation the operator was looking at (CROP-WB-003).
 * The snapshot comes from the authorized inspector selection when one is
 * open, otherwise from the delivered timeline events; a missing basis is an
 * explicit unavailable state, never a guess.
 */
function collectFraming(input: FeedbackMetadataInput): RawCollectorResult {
  const framingEvents = input.journey?.framing ?? [];
  if (!input.framingSelection && framingEvents.length === 0) {
    return {
      status: "unavailable",
      missing: ["framing.no_events_or_selection"],
      data: {},
    };
  }
  const snapshot = buildFeedbackFramingSnapshot({
    timestampMs: input.position.timestampMs,
    framingEvents,
    inspectorSelection: input.framingSelection ?? null,
  });
  const missing: string[] = [];
  if (snapshot.correlationStatus === "ambiguous") {
    missing.push("framing.ambiguous_scene_interval");
  }
  if (snapshot.correlationStatus === "unmatched") {
    missing.push("framing.no_matching_scene_interval");
  }
  if (snapshot.correlationStatus !== "exact" && !snapshot.attemptId) {
    missing.push("framing.exact_attempt_unavailable");
  }
  return {
    status:
      snapshot.correlationStatus === "exact"
        ? "complete"
        : snapshot.correlationStatus === "derived"
          ? missing.length > 0
            ? "partial"
            : "complete"
          : "partial",
    missing,
    data: framingSnapshotToJson(snapshot),
  };
}

/**
 * Resolve the scene at a millisecond position using authoritative frame ranges
 * first. All intervals are half-open; only the exact end of the final scene is
 * assigned back to that final scene.
 */
export function resolveFeedbackTimeline(
  envelope: Record<string, unknown> | undefined,
  timestampMs: number,
  media: readonly EchoflicksSessionMedia[] = [],
  sceneI2VByScene?: ReadonlyMap<SceneIdentity, SceneI2VAudit> | null,
): ResolvedFeedbackTimeline {
  const safeTimestampMs = finiteNonNegative(timestampMs) ?? 0;
  const contract = isRecord(envelope?.timeline_contract)
    ? envelope.timeline_contract
    : null;
  if (contract) {
    const target = isRecord(contract.target_duration)
      ? contract.target_duration
      : null;
    const renderProfile = isRecord(contract.render_profile)
      ? contract.render_profile
      : null;
    const fps =
      positiveNumber(target?.fps) ?? positiveNumber(renderProfile?.fps);
    const visual =
      isRecord(contract.timeline) && Array.isArray(contract.timeline.visual)
        ? contract.timeline.visual
        : [];
    if (fps && visual.length > 0) {
      const scenes = visual
        .map((value, index) => parseContractScene(value, index, fps))
        .filter((scene): scene is ResolvedFeedbackScene => scene !== null)
        .sort((left, right) => left.startFrame! - right.startFrame!);
      if (scenes.length > 0) {
        const timestampFrame = Math.floor((safeTimestampMs * fps) / 1_000);
        let matchIndex = scenes.findIndex(
          (scene) =>
            timestampFrame >= scene.startFrame! &&
            timestampFrame < scene.endFrameExclusive!,
        );
        if (matchIndex === -1 && safeTimestampMs === scenes.at(-1)?.endMs) {
          matchIndex = scenes.length - 1;
        }
        const targetFrames = nonNegativeInteger(target?.frames);
        return timelineResult({
          source: "timeline_contract",
          confidence: "exact",
          contractVersion: text(contract.contract_version),
          fps,
          targetFrames,
          targetDurationMs:
            milliseconds(target?.seconds) ??
            (targetFrames === null
              ? null
              : Math.round((targetFrames * 1_000) / fps)),
          timestampFrame,
          scenes,
          matchIndex,
          missing:
            matchIndex === -1
              ? ["timeline.timestamp_outside_contract"]
              : visual.length === scenes.length
                ? []
                : ["timeline.invalid_segments_ignored"],
        });
      }
    }
  }

  const legacyTimeline = Array.isArray(envelope?.scene_timeline)
    ? envelope.scene_timeline
    : [];
  if (legacyTimeline.length > 0) {
    const scenes = legacyTimeline
      .map(parseLegacyTimelineScene)
      .filter((scene): scene is ResolvedFeedbackScene => scene !== null)
      .sort((left, right) => left.startMs - right.startMs);
    if (scenes.length > 0) {
      const matchIndex = findSceneIndex(
        scenes,
        safeTimestampMs,
        (scene) => scene.startMs,
        (scene) => scene.endMs,
      );
      return timelineResult({
        source: "scene_timeline",
        confidence: "derived",
        contractVersion: null,
        fps: null,
        targetFrames: null,
        targetDurationMs: scenes.at(-1)?.endMs ?? null,
        timestampFrame: null,
        scenes,
        matchIndex,
        missing: [
          "timeline.frame_contract_unavailable",
          ...(matchIndex === -1 ? ["timeline.timestamp_outside_legacy"] : []),
        ],
      });
    }
  }

  const storyboard = buildStoryboard(
    envelope,
    [...media],
    sceneI2VByScene ? new Map(sceneI2VByScene) : undefined,
  );
  if (storyboard.length > 0) {
    let cursorMs = 0;
    const scenes = storyboard.map((scene) => {
      const durationMs = scene.durationSeconds
        ? Math.max(0, Math.round(scene.durationSeconds * 1_000))
        : SCENE_FALLBACK_MS;
      const result: ResolvedFeedbackScene = {
        id: scene.sceneId,
        index: scene.index,
        type: scene.type,
        startFrame: null,
        endFrameExclusive: null,
        startMs: cursorMs,
        endMs: cursorMs + durationMs,
        sourceFile: scene.sourceFile,
        assetId: null,
        transition: scene.transition,
        transitionFrames: null,
        transitionPlacement: null,
        conformPolicy: null,
        visualDescription: scene.titleText ?? scene.prompt,
      };
      cursorMs += durationMs;
      return result;
    });
    const matchIndex = findSceneIndex(
      scenes,
      safeTimestampMs,
      (scene) => scene.startMs,
      (scene) => scene.endMs,
    );
    return timelineResult({
      source: "storyboard",
      confidence: "estimated",
      contractVersion: null,
      fps: null,
      targetFrames: null,
      targetDurationMs: scenes.at(-1)?.endMs ?? null,
      timestampFrame: null,
      scenes,
      matchIndex,
      missing: [
        "timeline.contract_and_legacy_unavailable",
        "timeline.storyboard_duration_estimated",
        ...(matchIndex === -1 ? ["timeline.timestamp_outside_storyboard"] : []),
      ],
    });
  }

  return {
    source: "unavailable",
    confidence: "unavailable",
    contractVersion: null,
    fps: null,
    targetFrames: null,
    targetDurationMs: null,
    timestampFrame: null,
    scene: null,
    previousScene: null,
    nextScene: null,
    missing: ["timeline.unavailable_legacy"],
  };
}

/**
 * Recursively remove forbidden keys, signed URLs and non-JSON values while
 * enforcing bounded depth/width. Collectors use explicit allowlists first; this
 * is the final privacy backstop before persistence.
 */
export function sanitizeFeedbackMetadata(
  value: unknown,
): FeedbackJsonValue | undefined {
  return sanitizeValue(value, 0);
}

/** Throw when a snapshot contains a denied key/value or exceeds its bounds. */
export function assertNoSensitiveMetadata(value: unknown): void {
  assertSafeValue(value, 0, "$metadata");
}

function collectCore(input: FeedbackMetadataInput): RawCollectorResult {
  const { record, position } = input;
  const finalMedia = findFinalOutputMedia(record, input.media ?? []);
  const outputBucket =
    record.final_video_storage_bucket ??
    finalMedia?.storage_bucket ??
    finalMedia?.bucket;
  const outputPath =
    record.final_video_storage_path ??
    record.final_video_s3_key ??
    finalMedia?.storage_path ??
    finalMedia?.s3_key;
  const missing: string[] = [];
  if (!record.id && !record.source_id) missing.push("core.echo_id_unavailable");
  if (!outputPath && !finalMedia?.source_id) {
    missing.push("core.output_asset_identity_unavailable");
  }
  return {
    status: missing.length === 0 ? "complete" : "partial",
    missing,
    data: {
      echo: compactObject({
        id: identifier(record.id),
        sourceId: text(record.source_id),
        sessionId: text(record.session_id),
        workloadId: text(record.workload_id),
        echoType: text(record.echo_type),
        pipelineStatus: text(record.status),
        source: text(record.source),
        sourceConfidence: text(record.source_confidence),
        completedAt: text(record.completed_at),
      }),
      output: compactObject({
        assetId: identifier(finalMedia?.id) ?? text(finalMedia?.source_id),
        sourceId: text(finalMedia?.source_id),
        bucket: text(outputBucket),
        path: text(outputPath),
        mimeType: text(finalMedia?.mime_type) ?? "video/mp4",
        sizeBytes: finiteNonNegative(finalMedia?.size_bytes),
        createdAt: text(finalMedia?.created_at_source),
      }),
      player: compactObject({
        timestampMs: finiteNonNegative(position.timestampMs),
        videoDurationMs: finiteNonNegative(position.videoDurationMs),
        videoWidth: finiteNonNegative(position.videoWidth),
        videoHeight: finiteNonNegative(position.videoHeight),
        playbackRate: positiveNumber(position.playbackRate),
      }),
    },
  };
}

function collectTimeline(
  timeline: ResolvedFeedbackTimeline,
): RawCollectorResult {
  const status: FeedbackCollectorStatus =
    timeline.source === "timeline_contract" && timeline.scene
      ? timeline.missing.length === 0
        ? "complete"
        : "partial"
      : timeline.source === "unavailable"
        ? "unavailable"
        : "partial";
  return {
    status,
    missing: timeline.missing,
    data: {
      source: timeline.source,
      confidence: timeline.confidence,
      authority: "authored_timeline",
      deliveredOutputBinding: "unverified",
      contractVersion: timeline.contractVersion,
      fps: timeline.fps,
      targetFrames: timeline.targetFrames,
      targetDurationMs: timeline.targetDurationMs,
      timestampFrame: timeline.timestampFrame,
      scene: timeline.scene,
      adjacent: {
        previous: timeline.previousScene,
        next: timeline.nextScene,
      },
    },
  };
}

function collectI2V(
  input: FeedbackMetadataInput,
  timeline: ResolvedFeedbackTimeline,
): RawCollectorResult {
  const scene = timeline.scene;
  if (!scene) {
    return unavailable("i2v.scene_unavailable");
  }
  const audit = lookupSceneMap(input.sceneI2VByScene, scene.id, scene.index);
  const generated = findSceneGeneratedMedia(input.media ?? [], scene);
  const summary = generated?.i2v_prompt_audit_summary ?? null;
  const attempts = (input.journey?.i2v_prompt_attempts ?? [])
    .filter((attempt) => sceneMatches(attempt.scene_id, scene))
    .slice(-MAX_ATTEMPTS)
    .map((attempt) =>
      compactObject({
        id: text(attempt.audit_id) ?? identifier(attempt.id),
        attempt: finiteNonNegative(attempt.attempt),
        maxAttempts: finiteNonNegative(attempt.max_attempts),
        status: text(attempt.status),
        terminal: booleanValue(attempt.terminal),
        provider: text(attempt.provider),
        concreteProvider: text(attempt.concrete_provider),
        model: text(attempt.model),
        taskId: text(attempt.task_id),
        elapsedMs: finiteNonNegative(attempt.elapsed_ms),
        queueWaitMs: finiteNonNegative(attempt.queue_wait_ms),
        errorCode: text(attempt.error_code),
        retryable: booleanValue(attempt.retryable),
      }),
    );
  const data = compactObject({
    sceneId: scene.id,
    animated: booleanValue(audit?.animated),
    finalSceneType: text(audit?.finalSceneType),
    provider:
      text(audit?.provider) ??
      text(summary?.provider) ??
      text(generated?.provider),
    concreteProvider:
      text(audit?.concreteProvider) ??
      text(summary?.concrete_provider) ??
      text(generated?.concrete_provider),
    model: text(audit?.model) ?? text(summary?.model) ?? text(generated?.model),
    modelVersion:
      text(summary?.model_version) ?? text(generated?.model_version),
    effectivePositivePrompt: text(audit?.finalPositivePrompt),
    negativePrompt: text(audit?.negativePrompt),
    plannerPrompt: text(audit?.scenePrompt),
    sourcePreservationPrompt: text(audit?.sourcePreservationPrompt),
    positivePromptSha256:
      text(summary?.positive_prompt_sha256) ??
      text(generated?.positive_prompt_sha256),
    negativePromptSha256:
      text(summary?.negative_prompt_sha256) ??
      text(generated?.negative_prompt_sha256),
    promptChannel:
      text(summary?.prompt_channel) ?? text(generated?.prompt_channel),
    auditId: text(summary?.audit_id) ?? text(generated?.i2v_prompt_audit_id),
    taskId:
      text(audit?.taskId) ?? text(summary?.task_id) ?? text(generated?.task_id),
    attemptCount:
      finiteNonNegative(audit?.attemptCount) ??
      finiteNonNegative(summary?.attempt) ??
      (attempts.length || null),
    fallback: compactObject({
      applied:
        booleanValue(audit?.fallbackApplied) ?? booleanValue(summary?.fallback),
      from: text(audit?.fallbackFrom) ?? text(summary?.fallback_from),
      to: text(audit?.fallbackTo),
      reason: text(audit?.fallbackReason) ?? text(summary?.error_code),
    }),
    rai: compactObject({
      filtered: booleanValue(audit?.raiFiltered),
      reason: text(audit?.raiReason),
    }),
    referenceFrames: referenceFramesOf(audit, summary),
    attempts,
    source: text(audit?.source) ?? (summary ? "media_summary" : null),
  });
  const hasData = Object.keys(data).some(
    (key) => !["sceneId", "attempts", "fallback", "rai"].includes(key),
  );
  if (!hasData) return unavailable("i2v.audit_unavailable_legacy");
  const missing: string[] = [];
  if (!data.provider) missing.push("i2v.provider_unavailable");
  if (!data.effectivePositivePrompt && !data.positivePromptSha256) {
    missing.push("i2v.effective_prompt_unavailable");
  }
  return {
    status: missing.length === 0 ? "complete" : "partial",
    missing,
    data,
  };
}

function collectMedia(
  input: FeedbackMetadataInput,
  timeline: ResolvedFeedbackTimeline,
): RawCollectorResult {
  const scene = timeline.scene;
  if (!scene) return unavailable("media.scene_unavailable");
  const allMedia = input.media ?? [];
  const source = findSceneSourceMedia(allMedia, scene);
  const generated = findSceneGeneratedMedia(allMedia, scene);
  const storyboard = buildStoryboard(
    input.envelope,
    [...allMedia],
    input.sceneI2VByScene ? new Map(input.sceneI2VByScene) : undefined,
  );
  const usedIds = new Set(
    storyboard.flatMap((item) =>
      item.media ? [String(item.media.id), item.media.source_id] : [],
    ),
  );
  const currentSourceIdentity = source
    ? [String(source.id), source.source_id]
    : [];
  const reuseCount = storyboard.filter(
    (item) =>
      item.media &&
      currentSourceIdentity.some(
        (identity) =>
          identity === String(item.media?.id) ||
          identity === item.media?.source_id,
      ),
  ).length;
  const framing = (input.journey?.framing ?? [])
    .filter((event) => sceneMatches(event.scene_id, scene))
    .slice(-5)
    .map(framingEventSnapshot);
  const data = compactObject({
    sceneId: scene.id,
    source: mediaSnapshot(source),
    generated: mediaSnapshot(generated),
    adjacent: compactObject({
      previous: sceneSummary(timeline.previousScene),
      current: sceneSummary(scene),
      next: sceneSummary(timeline.nextScene),
    }),
    sourceReuseCount: reuseCount || null,
    unusedUploadedMedia: allMedia
      .filter(
        (item) =>
          item.kind === "uploaded_media" &&
          !usedIds.has(String(item.id)) &&
          !usedIds.has(item.source_id),
      )
      .slice(0, MAX_CONTEXT_MEDIA)
      .map((item) =>
        compactObject({
          id: identifier(item.id),
          sourceId: text(item.source_id),
          fileName: mediaFilename(item),
          orientation: text(item.orientation),
          width: finiteNonNegative(item.width),
          height: finiteNonNegative(item.height),
        }),
      ),
    framing,
    echoFramingSummary: compactObject({
      status: text(input.record.framing_status),
      atRisk: booleanValue(input.record.framing_at_risk),
      failedSceneCount: finiteNonNegative(
        input.record.framing_failed_scene_count,
      ),
      repairedSceneCount: finiteNonNegative(
        input.record.framing_repaired_scene_count,
      ),
      fallbackSceneCount: finiteNonNegative(
        input.record.framing_fallback_scene_count,
      ),
    }),
    qcFindings: (input.record.qc_top_findings ?? [])
      .filter((finding) => sceneMatches(finding.scene_id, scene))
      .slice(0, 20)
      .map((finding) =>
        compactObject({
          code: text(finding.code),
          severity: text(finding.severity),
          layer: text(finding.layer_name),
          message: text(finding.message),
          confidence: finiteNonNegative(finding.confidence),
        }),
      ),
  });
  const missing: string[] = [];
  if (!source) missing.push("media.source_asset_unavailable");
  if (!generated) missing.push("media.generated_asset_unavailable");
  return {
    status:
      source && generated
        ? "complete"
        : Object.keys(data).length
          ? "partial"
          : "unavailable",
    missing,
    data,
  };
}

/**
 * NWP-010: the planned narration window containing a scene, when the delivery
 * envelope carries the real grouping.
 *
 * Until the honest-envelope change the envelope fabricated one window per scene,
 * so a snapshot built from it would have told the reviewer every shot was its own
 * chapter. `windows_source: "planner"` is the marker that the grouping is real;
 * without it this returns null and the field is simply absent.
 */
function plannedWindowForScene(
  envelope: unknown,
  sceneId: string | number | null,
): Record<string, unknown> | null {
  if (sceneId === null || !isRecord(envelope)) return null;
  const rows = envelope.scene_timeline;
  if (!Array.isArray(rows)) return null;
  for (const row of rows) {
    if (!isRecord(row) || row.windows_source !== "planner") continue;
    if (String(row.id ?? row.scene_id ?? "") !== String(sceneId)) continue;
    const window = row.recommended_narration_window;
    if (!isRecord(window)) return null;
    return compactObject({
      id: nonNegativeInteger(window.id),
      sceneIds: Array.isArray(window.scene_ids)
        ? window.scene_ids.map((value) => sceneIdText(value)).filter(Boolean)
        : null,
      anchorSceneId: sceneIdText(window.anchor_scene_id),
      boundaryReason: text(window.boundary_reason),
      oversized: window.oversized === true ? true : null,
      beatIndex: nonNegativeInteger(window.beat_index),
    });
  }
  return null;
}

function sceneIdText(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return text(value);
}

function collectAudio(
  input: FeedbackMetadataInput,
  timeline: ResolvedFeedbackTimeline,
): RawCollectorResult {
  const script = directorScriptOf(input.envelope);
  if (!script) return unavailable("audio.script_envelope_unavailable");
  const resolved = buildNarrationTimeline({
    envelope: input.envelope,
    manifest: input.manifest,
    sync:
      input.sync ??
      buildSyncTimeline({
        envelope: input.envelope,
        manifest: input.manifest,
        events: [...(input.events ?? [])],
        videoDurationSeconds:
          input.position.videoDurationMs == null
            ? null
            : input.position.videoDurationMs / 1000,
        sessionWorkloadId: input.record.workload_id,
        finalVideoStoragePath:
          input.finalVideoStoragePath ?? input.record.final_video_storage_path,
      }),
    sessionWorkloadId: input.record.workload_id,
    finalVideoStoragePath:
      input.finalVideoStoragePath ?? input.record.final_video_storage_path,
  });
  const narration = isNarrationTimeline(resolved) ? resolved : null;
  const actual = new Map(
    narration?.lines.map((line) => [line.index, line]) ?? [],
  );
  const finalScript =
    narration?.source === "manifest_narration" && isRecord(input.manifest)
      ? directorScriptOf({ script: input.manifest.resolved_script })
      : null;
  const cues = (finalScript?.audio_content ?? script.audio_content ?? [])
    .map((cue, index) => ({
      index,
      startMs: actual.has(index)
        ? Math.round(actual.get(index)!.startSeconds * 1000)
        : (milliseconds(cue.start_second) ?? 0),
      endMs: actual.has(index)
        ? Math.round(actual.get(index)!.endSeconds * 1000)
        : null,
      text: actual.get(index)?.text ?? text(cue.voice_over_prompt),
      // NWP-010: the window the writer bound this line to, so an operator filing
      // "the narration is ahead of the picture" can see whether the pipeline
      // already knew. Allowlisted scalars only - never the raw binding blob.
      windowId: nonNegativeInteger(cue.window_id),
      anchorSceneId: sceneIdText(
        isRecord(cue.binding) ? cue.binding.anchor_scene_id : null,
      ),
      bindingMode: isRecord(cue.binding) ? text(cue.binding.mode) : null,
    }))
    .filter((cue) => cue.text !== null);
  const scene = timeline.scene;
  const active = scene
    ? cues.filter((cue) =>
        cue.endMs != null
          ? cue.startMs <= input.position.timestampMs &&
            cue.endMs > input.position.timestampMs
          : cue.startMs >= scene.startMs && cue.startMs < scene.endMs,
      )
    : [];
  const nearby = cues
    .filter(
      (cue) => Math.abs(cue.startMs - input.position.timestampMs) <= 5_000,
    )
    .slice(0, 10);
  const narrationWindow = plannedWindowForScene(
    input.envelope,
    scene?.id ?? null,
  );
  const data = compactObject({
    narrationWindow,
    narrationSource: narration?.source ?? "unavailable",
    narrationConfidence: narration?.confidence ?? "unavailable",
    narrationPlan:
      narration?.source === "manifest_narration"
        ? readNarrationPlan(input.manifest)
        : null,
    activeVoiceoverCues: active,
    nearbyVoiceoverCues: nearby,
    voice: compactObject({
      generationType: text(script.voice_generation?.gen_type),
      voiceId: text(script.voice_generation?.voice_id),
    }),
    music: compactObject({
      positiveGlobalStyles:
        script.music_prompt?.composition_plan?.positive_global_styles?.slice(
          0,
          10,
        ) ?? [],
      detectedBpm: detectedMusicBpm([...(input.events ?? [])]),
    }),
  });
  const missing: string[] = [];
  if (!narration) missing.push("audio.realized_narration_unavailable");
  if (!script.voice_generation?.voice_id)
    missing.push("audio.voice_identity_unavailable");
  if (!cues.length)
    missing.push("audio.narration_cues_not_captured_or_not_applicable");
  const manifest = isRecord(input.manifest) ? input.manifest : {};
  if (!manifest.audio_mix) missing.push("audio.final_mix_receipt_unavailable");
  return { status: missing.length ? "partial" : "complete", missing, data };
}

function collectConfiguration(
  input: FeedbackMetadataInput,
): RawCollectorResult {
  const sources = configurationSources(input);
  const releaseId = firstText(sources, [
    "config_release_id",
    "configuration_release_id",
    "frozen_release_id",
    "release_id",
  ]);
  const digest = firstText(sources, [
    "config_digest",
    "configuration_digest",
    "frozen_digest",
    "digest",
  ]);
  const schemaVersion = firstText(sources, [
    "config_schema_version",
    "configuration_schema_version",
    "runtime_config_schema_version",
    "schema_version",
  ]);
  const decisions = sources
    .flatMap((source) => decisionProvenance(source.value, source.name))
    .slice(0, 30);
  const data = compactObject({
    releaseId,
    digest,
    schemaVersion,
    provenanceSource:
      sources.find((source) =>
        [releaseId, digest, schemaVersion].some(
          (needle) => needle && Object.values(source.value).includes(needle),
        ),
      )?.name ?? null,
    decisions,
  });
  if (!releaseId && !digest && !schemaVersion && decisions.length === 0) {
    return unavailable("configuration.frozen_provenance_unavailable_legacy");
  }
  const missing: string[] = [];
  if (!releaseId) missing.push("configuration.release_id_unavailable");
  if (!digest) missing.push("configuration.digest_unavailable");
  if (!schemaVersion) missing.push("configuration.schema_version_unavailable");
  return {
    status: missing.length === 0 ? "complete" : "partial",
    missing,
    data,
  };
}

function parseContractScene(
  value: unknown,
  fallbackIndex: number,
  fps: number,
): ResolvedFeedbackScene | null {
  if (!isRecord(value) || !isRecord(value.planned_range)) return null;
  const startFrame = nonNegativeInteger(value.planned_range.start_frame);
  const durationFrames = nonNegativeInteger(
    value.planned_range.duration_frames,
  );
  const providedEnd = nonNegativeInteger(
    value.planned_range.end_frame_exclusive,
  );
  const endFrameExclusive =
    startFrame === null
      ? null
      : durationFrames !== null
        ? startFrame + durationFrames
        : providedEnd;
  if (
    startFrame === null ||
    endFrameExclusive === null ||
    endFrameExclusive < startFrame
  ) {
    return null;
  }
  return {
    id: identity(value.scene_id),
    index: nonNegativeInteger(value.scene_index) ?? fallbackIndex,
    type: text(value.scene_type),
    startFrame,
    endFrameExclusive,
    startMs: Math.round((startFrame * 1_000) / fps),
    endMs: Math.round((endFrameExclusive * 1_000) / fps),
    sourceFile: text(value.source_file),
    assetId: text(value.asset_id),
    transition: text(value.transition_out),
    transitionFrames: nonNegativeInteger(value.transition_out_frames),
    transitionPlacement: text(value.transition_out_placement),
    conformPolicy: text(value.conform_policy),
    visualDescription: text(value.visual_description),
  };
}

function parseLegacyTimelineScene(
  value: unknown,
  index: number,
): ResolvedFeedbackScene | null {
  if (!isRecord(value)) return null;
  const startMs = milliseconds(value.start_second);
  const endMs = milliseconds(value.end_second);
  if (startMs === null || endMs === null || endMs < startMs) return null;
  return {
    id: identity(value.id),
    index,
    type: text(value.scene_type),
    startFrame: null,
    endFrameExclusive: null,
    startMs,
    endMs,
    sourceFile: text(value.source_file),
    assetId: text(value.asset_id),
    transition: text(value.transition),
    transitionFrames: null,
    transitionPlacement: null,
    conformPolicy: null,
    visualDescription: text(value.visual_description),
  };
}

function timelineResult(args: {
  source: Exclude<FeedbackTimelineSource, "unavailable">;
  confidence: Exclude<FeedbackTimelineConfidence, "unavailable">;
  contractVersion: string | null;
  fps: number | null;
  targetFrames: number | null;
  targetDurationMs: number | null;
  timestampFrame: number | null;
  scenes: ResolvedFeedbackScene[];
  matchIndex: number;
  missing: string[];
}): ResolvedFeedbackTimeline {
  return {
    source: args.source,
    confidence: args.confidence,
    contractVersion: args.contractVersion,
    fps: args.fps,
    targetFrames: args.targetFrames,
    targetDurationMs: args.targetDurationMs,
    timestampFrame: args.timestampFrame,
    scene: args.matchIndex >= 0 ? args.scenes[args.matchIndex] : null,
    previousScene:
      args.matchIndex > 0 ? args.scenes[args.matchIndex - 1] : null,
    nextScene:
      args.matchIndex >= 0 && args.matchIndex < args.scenes.length - 1
        ? args.scenes[args.matchIndex + 1]
        : null,
    missing: args.missing,
  };
}

function findSceneIndex<T>(
  scenes: readonly T[],
  position: number,
  start: (scene: T) => number,
  end: (scene: T) => number,
): number {
  const matched = scenes.findIndex(
    (scene) => position >= start(scene) && position < end(scene),
  );
  if (matched >= 0) return matched;
  const last = scenes.at(-1);
  return last && position === end(last) ? scenes.length - 1 : -1;
}

function runCollector(collector: () => RawCollectorResult): CollectorResult {
  try {
    const result = collector();
    const sanitized = sanitizeFeedbackMetadata(result.data);
    return {
      status: result.status,
      missing: [...new Set(result.missing)].slice(
        0,
        ECHO_FEEDBACK_LIMITS.METADATA_ARRAY_ITEMS,
      ),
      data: isRecord(sanitized) ? (sanitized as FeedbackJsonObject) : {},
    };
  } catch {
    return {
      status: "unavailable",
      missing: ["collector.failed"],
      data: {},
    };
  }
}

function summaryOf(result: CollectorResult) {
  return { status: result.status, missing: result.missing };
}

function unavailable(reason: string): RawCollectorResult {
  return { status: "unavailable", missing: [reason], data: {} };
}

function sanitizeValue(
  value: unknown,
  depth: number,
): FeedbackJsonValue | undefined {
  if (depth > ECHO_FEEDBACK_LIMITS.METADATA_DEPTH || value === undefined) {
    return undefined;
  }
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    if (SIGNED_OR_SECRET_URL.test(value)) return undefined;
    return value.slice(0, ECHO_FEEDBACK_LIMITS.METADATA_STRING_CHARACTERS);
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, ECHO_FEEDBACK_LIMITS.METADATA_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, depth + 1))
      .filter((item): item is FeedbackJsonValue => item !== undefined);
  }
  if (!isRecord(value)) return undefined;
  const output: FeedbackJsonObject = {};
  for (const [key, item] of Object.entries(value).slice(
    0,
    ECHO_FEEDBACK_LIMITS.METADATA_OBJECT_KEYS,
  )) {
    if (isSensitiveKey(key)) continue;
    const sanitized = sanitizeValue(item, depth + 1);
    if (sanitized !== undefined) output[key] = sanitized;
  }
  return output;
}

function assertSafeValue(value: unknown, depth: number, path: string): void {
  if (depth > ECHO_FEEDBACK_LIMITS.METADATA_DEPTH) {
    throw new TypeError(`${path} exceeds the metadata depth limit`);
  }
  if (value === undefined) {
    throw new TypeError(`${path} contains undefined`);
  }
  if (value === null || typeof value === "boolean") return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError(`${path} is not finite`);
    return;
  }
  if (typeof value === "string") {
    if (value.length > ECHO_FEEDBACK_LIMITS.METADATA_STRING_CHARACTERS) {
      throw new TypeError(`${path} exceeds the metadata string limit`);
    }
    if (SIGNED_OR_SECRET_URL.test(value)) {
      throw new TypeError(`${path} contains a signed or secret URL`);
    }
    return;
  }
  if (Array.isArray(value)) {
    if (value.length > ECHO_FEEDBACK_LIMITS.METADATA_ARRAY_ITEMS) {
      throw new TypeError(`${path} exceeds the metadata array limit`);
    }
    value.forEach((item, index) =>
      assertSafeValue(item, depth + 1, `${path}[${index}]`),
    );
    return;
  }
  if (!isRecord(value)) throw new TypeError(`${path} is not JSON`);
  const entries = Object.entries(value);
  if (entries.length > ECHO_FEEDBACK_LIMITS.METADATA_OBJECT_KEYS) {
    throw new TypeError(`${path} exceeds the metadata object-key limit`);
  }
  for (const [key, item] of entries) {
    if (isSensitiveKey(key)) throw new TypeError(`${path}.${key} is sensitive`);
    assertSafeValue(item, depth + 1, `${path}.${key}`);
  }
}

function isSensitiveKey(key: string): boolean {
  const normalized = key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .toLowerCase()
    .replace(/^_+|_+$/g, "");
  return (
    SENSITIVE_KEY_NAMES.has(normalized) ||
    normalized.startsWith("authorization_") ||
    normalized.split("_").includes("cookie") ||
    normalized.endsWith("_api_key") ||
    normalized.endsWith("_token") ||
    normalized.endsWith("_secret") ||
    normalized.endsWith("_password") ||
    normalized.endsWith("_credential") ||
    normalized.endsWith("_credentials") ||
    normalized.endsWith("_webhook_url") ||
    normalized.endsWith("_signed_url") ||
    normalized.endsWith("_presigned_url") ||
    normalized.endsWith("_phone") ||
    normalized.endsWith("_phone_number") ||
    normalized.endsWith("_email")
  );
}

function findFinalOutputMedia(
  record: EchoflicksVideoSession,
  media: readonly EchoflicksSessionMedia[],
): EchoflicksSessionMedia | undefined {
  const target = record.final_video_storage_path ?? record.final_video_s3_key;
  return (
    media.find(
      (item) =>
        target && (item.storage_path === target || item.s3_key === target),
    ) ??
    media.find(
      (item) =>
        item.kind === "final_video" || item.asset_role === "final_video",
    )
  );
}

function findSceneSourceMedia(
  media: readonly EchoflicksSessionMedia[],
  scene: ResolvedFeedbackScene,
): EchoflicksSessionMedia | undefined {
  const sourceFile = scene.sourceFile?.toLowerCase();
  return (
    media.find(
      (item) =>
        item.kind === "uploaded_media" &&
        sourceFile &&
        mediaFilename(item)?.toLowerCase() === sourceFile,
    ) ??
    media.find(
      (item) =>
        item.kind === "uploaded_media" && sceneMatches(item.scene_id, scene),
    )
  );
}

function findSceneGeneratedMedia(
  media: readonly EchoflicksSessionMedia[],
  scene: ResolvedFeedbackScene,
): EchoflicksSessionMedia | undefined {
  return media.find(
    (item) =>
      item.kind !== "uploaded_media" &&
      item.kind !== "final_video" &&
      sceneMatches(item.scene_id, scene),
  );
}

function lookupSceneMap(
  map: ReadonlyMap<SceneIdentity, SceneI2VAudit> | null | undefined,
  sceneId: SceneIdentity | null,
  sceneIndex: number,
): SceneI2VAudit | null {
  if (!map) return null;
  if (sceneId !== null) {
    const direct = map.get(sceneId) ?? map.get(String(sceneId));
    if (direct) return direct;
  }
  return map.get(`index:${sceneIndex}`) ?? null;
}

function referenceFramesOf(
  audit: SceneI2VAudit | null,
  summary: I2VPromptAuditSummary | null,
): unknown {
  if (audit?.referenceFrames) {
    return {
      firstFrame: audit.referenceFrames.firstFrame,
      endFrame: audit.referenceFrames.endFrame,
      hasEndFrame: audit.referenceFrames.hasEndFrame,
      referenceFiles: audit.referenceFrames.referenceFiles?.slice(0, 8),
    };
  }
  return summary?.reference_frames
    ? pickReferenceFrames(summary.reference_frames)
    : null;
}

function pickReferenceFrames(value: Record<string, unknown>): unknown {
  const first = isRecord(value.first_frame) ? value.first_frame : null;
  const end = isRecord(value.end_frame) ? value.end_frame : null;
  return compactObject({
    firstFrame: first
      ? compactObject({
          fileName: text(first.file_name),
          storageKey: text(first.storage_key),
          role: text(first.role),
        })
      : null,
    endFrame: end
      ? compactObject({
          fileName: text(end.file_name),
          storageKey: text(end.storage_key),
          role: text(end.role),
        })
      : null,
    hasEndFrame: booleanValue(value.has_end_frame),
    referenceFiles: Array.isArray(value.reference_files)
      ? value.reference_files
          .filter((item): item is string => typeof item === "string")
          .slice(0, 8)
      : [],
  });
}

function mediaSnapshot(item: EchoflicksSessionMedia | undefined): unknown {
  if (!item) return null;
  return compactObject({
    id: identifier(item.id),
    sourceId: text(item.source_id),
    kind: text(item.kind),
    assetRole: text(item.asset_role),
    fileName: mediaFilename(item),
    bucket: text(item.storage_bucket) ?? text(item.bucket),
    path: text(item.storage_path) ?? text(item.s3_key) ?? text(item.local_path),
    mimeType: text(item.mime_type),
    sizeBytes: finiteNonNegative(item.size_bytes),
    width: finiteNonNegative(item.width),
    height: finiteNonNegative(item.height),
    orientation: text(item.orientation),
    caption: text(item.gemini_caption),
    description: text(item.gemini_description),
    faceSensitive: booleanValue(item.face_sensitive),
    faceSensitiveReason: text(item.face_sensitive_reason),
    expressionQcMode: text(item.expression_qc_mode),
    provider: text(item.provider),
    concreteProvider: text(item.concrete_provider),
    model: text(item.model),
    modelVersion: text(item.model_version),
    taskId: text(item.task_id),
    createdAt: text(item.created_at_source),
  });
}

function framingEventSnapshot(event: EchoflicksSessionFramingEvent): unknown {
  return compactObject({
    eventType: text(event.event_type),
    schemaVersion: text(event.schema_version),
    framingPlanId: text(event.framing_plan_id),
    strategy: text(event.strategy),
    providerAspect: text(event.provider_aspect),
    deliveryAspect: text(event.delivery_aspect),
    fallbackReason: text(event.fallback_reason),
    subjects: (event.subject_summary ?? []).slice(0, 10).map((subject) =>
      compactObject({
        id: text(subject.subject_id),
        label: text(subject.label),
        kind: text(subject.kind),
        priority: text(subject.priority),
        confidence: finiteNonNegative(subject.confidence),
        identityRequired: booleanValue(subject.identity_required),
      }),
    ),
    qc: event.qc_summary
      ? compactObject({
          status: text(event.qc_summary.status),
          issueCodes: event.qc_summary.issue_codes?.slice(0, 20) ?? [],
          checkedSpaceId: text(event.qc_summary.checked_space_id),
          sampleCount: finiteNonNegative(event.qc_summary.sample_count),
          repairAttempt: finiteNonNegative(event.qc_summary.repair_attempt),
        })
      : null,
    occurredAt: text(event.occurred_at),
  });
}

function sceneSummary(scene: ResolvedFeedbackScene | null): unknown {
  return scene
    ? compactObject({
        id: scene.id,
        index: scene.index,
        type: scene.type,
        sourceFile: scene.sourceFile,
        assetId: scene.assetId,
        startMs: scene.startMs,
        endMs: scene.endMs,
      })
    : null;
}

function configurationSources(
  input: FeedbackMetadataInput,
): Array<{ name: string; value: JsonRecord }> {
  const sources: Array<{ name: string; value: JsonRecord }> = [];
  const add = (name: string, value: unknown) => {
    if (isRecord(value)) sources.push({ name, value });
  };
  add("script_envelope", input.envelope);
  add("script_envelope.configuration", input.envelope?.configuration);
  add("script_envelope.config", input.envelope?.config);
  add("session.raw_metadata", input.record.raw_metadata);
  for (const event of input.events ?? []) {
    if (!/(?:config|release|snapshot)/i.test(event.event_type)) continue;
    const payload = isRecord(event.raw_payload?.payload)
      ? event.raw_payload.payload
      : event.raw_payload;
    add(`event:${event.event_type}`, payload);
  }
  return sources;
}

function firstText(sources: Array<{ value: JsonRecord }>, keys: string[]) {
  for (const source of sources) {
    for (const key of keys) {
      const value = text(source.value[key]);
      if (value) return value;
    }
  }
  return null;
}

function decisionProvenance(value: JsonRecord, source: string): unknown[] {
  const candidate = value.resolved_decisions ?? value.config_decisions;
  if (!isRecord(candidate)) return [];
  return Object.entries(candidate)
    .filter(([key]) => !isSensitiveKey(key))
    .slice(0, 30)
    .map(([key, decision]) => {
      const record = isRecord(decision) ? decision : null;
      return compactObject({
        key,
        source: record ? (text(record.source) ?? source) : source,
        definitionId: record ? text(record.definition_id) : null,
        releaseId: record ? text(record.release_id) : null,
        decisionId: record ? text(record.decision_id) : null,
      });
    });
}

function compactObject(value: Record<string, unknown>): JsonRecord {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== undefined && item !== null,
    ),
  );
}

function normalizedCaptureTime(value: string | undefined): string {
  if (value && Number.isFinite(Date.parse(value))) {
    return new Date(value).toISOString();
  }
  return new Date().toISOString();
}

function sceneMatches(value: unknown, scene: ResolvedFeedbackScene): boolean {
  if (value === null || value === undefined || scene.id === null) return false;
  return String(value) === String(scene.id);
}

function mediaFilename(item: EchoflicksSessionMedia): string | null {
  const path = item.s3_key ?? item.storage_path ?? item.local_path;
  return path?.split(/[\\/]/).at(-1) ?? null;
}

function identity(value: unknown): SceneIdentity | null {
  return typeof value === "string" || typeof value === "number" ? value : null;
}

function identifier(value: unknown): string | number | null {
  return typeof value === "string" || typeof value === "number" ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function booleanValue(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function positiveNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function finiteNonNegative(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function nonNegativeInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : null;
}

function milliseconds(seconds: unknown): number | null {
  const value = finiteNonNegative(seconds);
  return value === null ? null : Math.round(value * 1_000);
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
